import { foreignMessage, foreignValue, isPlainRecord, listed, report, type Place } from "./checks.ts";
import { checkCreatureLevels, checkXpGap, creatureLevelOf, xpGapOf } from "./creature-level-records.ts";
import { checkDiminishingWindow, checkPenalties, effectOf } from "./effect-records.ts";
import { checkItems } from "./item-records.ts";
import { checkLooks } from "./look-records.ts";
import { checkOnHit } from "./on-hit-records.ts";
import { checkProjectileDodge, checkProjectileWeapons } from "./projectile-records.ts";
import { constantOf, critOf, levelOf, resourceOf } from "./pool-records.ts";
import { abilityOf, weaponOf } from "./records.ts";
import { checkDiagonalMoves, checkRunRestart, checkRunSpeed } from "./speed-records.ts";
import { checkAttributeCap } from "./attribute-records.ts";
import { checkTravelFocus } from "./travel-records.ts";
import { checkXpRates } from "./xp-records.ts";
import { backgroundOf } from "./background-records.ts";
import { BACKGROUND_IDS, BAR_SLOTS, CONSTANT_IDS, CRIT_IDS, RESOURCE_IDS, RULES_ID_PATTERN, RULES_TABLES, type Ability, type Constant, type CountConstantId, type Level, type RulesProblem, type TickConstantId, type RulesTableName, type RulesVerdict, type Weapon } from "./types.ts";

/**
 * The rules validator (ADR-069 decision 5): pure, and the only judge of a set of tables, whether the loader read
 * them from the build or a later one reads them from a database. It refuses anything that is not JSON data,
 * every field it does not know and every field missing, numbers that are not whole or out of bounds, members
 * outside the closed sets, malformed and duplicate ids and references to ids no table holds, and reports every
 * problem it finds, each naming its table and its record's id.
 */

/**
 * A record that passed its checks, with the name its problems would carry.
 */
interface Passed<T> {
  readonly name: string;
  readonly record: T;
}

/**
 * Words a problem as its table, its record's id and what is wrong.
 *
 * @param problem The problem.
 * @returns `<table> <id>: <what>`, or `<table>: <what>` for the table itself.
 * @example
 * describeProblem({ table: "abilities", id: "bash", message: "the stamina cost is -5, and a cost is never negative" });
 * // "abilities bash: the stamina cost is -5, and a cost is never negative"
 */
export function describeProblem(problem: RulesProblem): string {
  return problem.id === null ? `${problem.table}: ${problem.message}` : `${problem.table} ${problem.id}: ${problem.message}`;
}

/**
 * The name a record goes by in its problems: its `id`, or its `level`, when that is well formed, else its place.
 * The field is read through its descriptor, so no getter runs.
 *
 * @param value The record.
 * @param index Its place in the table.
 * @param key The field that names it.
 * @returns The name, or `#n` for the n-th record.
 */
function recordName(value: unknown, index: number, key: string): string {
  const named: unknown = isPlainRecord(value) ? Object.getOwnPropertyDescriptor(value, key)?.value : undefined;
  if (typeof named === "string" && RULES_ID_PATTERN.test(named)) {
    return named;
  }
  if (typeof named === "number" && Number.isInteger(named) && named >= 1) {
    return String(named);
  }
  return `#${index + 1}`;
}

/**
 * The well-formed names a table's records carry, read before any record is checked, so a reference is judged
 * against every table whatever order the tables are read in.
 *
 * @param records The table.
 * @param key The field that names a record.
 * @returns The names.
 */
function namesIn(records: readonly unknown[] | undefined, key = "id"): ReadonlySet<string> {
  const names = new Set<string>();
  for (const [index, record] of (records ?? []).entries()) {
    const named = recordName(record, index, key);
    if (!named.startsWith("#")) {
      names.add(named);
    }
  }
  return names;
}

/**
 * Checks each record of a table in turn: a value that is not JSON data first, then the record's own fields, then
 * that its name is not held twice.
 *
 * @param table The table's name.
 * @param records Its records.
 * @param problems Where problems go.
 * @param read Checks one record's fields and builds it.
 * @param key The field that names a record.
 * @returns Each record that passed, with its name, a second of one name left out.
 */
function checkRecords<T>(table: RulesTableName, records: readonly unknown[], problems: RulesProblem[], read: (place: Place, record: Readonly<Record<string, unknown>>) => T | null, key = "id"): Passed<T>[] {
  const passed: Passed<T>[] = [];
  const seen = new Set<string>();
  for (const [index, value] of records.entries()) {
    const recordId = recordName(value, index, key);
    const place: Place = { table, id: recordId, problems };
    const foreign = foreignValue(value, "");
    if (foreign !== null) {
      report(place, foreignMessage(foreign, "the record"));
      continue;
    }
    if (!isPlainRecord(value)) {
      report(place, "it is not a record of fields");
      continue;
    }
    const built = read(place, value);
    if (!recordId.startsWith("#") && seen.has(recordId)) {
      report(place, key === "level" ? "the level is held twice" : "the id is held twice");
      continue;
    }
    seen.add(recordId);
    if (built !== null) {
      passed.push({ name: recordId, record: built });
    }
  }
  return passed;
}

/**
 * Reports each member of a closed set a table lacks.
 *
 * @param place The table's place.
 * @param held The ids its records hold.
 * @param set The set every member of which it must hold.
 */
function reportMissing(place: Place, held: ReadonlySet<string>, set: readonly string[]): void {
  for (const id of set) {
    if (!held.has(id)) {
      report(place, `${id} is missing`);
    }
  }
}

/**
 * Reports levels that do not run 1, 2, 3 and so on, and a table with no level. A record whose level is not a
 * whole number of at least 1 has been reported already, and a level held twice is reported where it is read.
 *
 * @param table The table that is checked: the player levels or the creature levels.
 * @param records The table.
 * @param problems Where problems go.
 */
function checkLevelsRun(table: "levels" | "creature_levels", records: readonly unknown[], problems: RulesProblem[]): void {
  if (records.length === 0) {
    report({ table, id: null, problems }, "the table holds no level; it starts at 1");
    return;
  }
  const seen = new Set<string>();
  let previous = 0;
  for (const [index, record] of records.entries()) {
    const named = recordName(record, index, "level");
    if (named.startsWith("#") || seen.has(named)) {
      continue;
    }
    seen.add(named);
    const level = Number(named);
    if (level !== previous + 1) {
      report({ table, id: named, problems }, previous === 0 ? `level ${level} comes first; the levels start at 1` : `level ${level} follows level ${previous}; the levels run 1, 2, 3 and so on`);
    }
    previous = level;
  }
}

/**
 * Reports a level table that does not climb as ADR-083 decision 1 has it: level 1 reached at 0 XP, each level's XP
 * above the one before it, and no pool and no `damagePercent` below the level before. Only rows that passed their own
 * checks are compared, each with the one before it among them.
 *
 * @param levels The levels that passed their checks, in the table's order.
 * @param problems Where problems go.
 */
function checkLevelsClimb(levels: readonly Level[], problems: RulesProblem[]): void {
  let previous: Level | null = null;
  for (const level of levels) {
    const place = { table: "levels" as const, id: String(level.level), problems };
    if (level.level === 1 && level.xp !== 0) {
      report(place, `level 1 is reached at ${level.xp} XP; it is reached at 0`);
    }
    if (previous !== null) {
      if (level.xp <= previous.xp) {
        report(place, `level ${level.level} is reached at ${level.xp} XP, not more than level ${previous.level}'s ${previous.xp}; the XP rises with each level`);
      }
      if (level.damagePercent < previous.damagePercent) {
        report(place, `level ${level.level}'s damagePercent ${level.damagePercent} is below level ${previous.level}'s ${previous.damagePercent}; it never falls`);
      }
      for (const id of RESOURCE_IDS) {
        if (level.pools[id] < previous.pools[id]) {
          report(place, `level ${level.level}'s pools.${id} ${level.pools[id]} is below level ${previous.level}'s ${previous.pools[id]}; it never falls`);
        }
      }
    }
    previous = level;
  }
}

/**
 * Reports a set of weapons with no starting weapon, and every starting weapon after the first.
 *
 * @param weapons The weapons that passed their checks.
 * @param problems Where problems go.
 */
function checkStarting(weapons: readonly Passed<Weapon>[], problems: RulesProblem[]): void {
  const starting = weapons.filter(({ record }) => record.starting);
  const first = starting[0];
  if (first === undefined) {
    report({ table: "weapons", id: null, problems }, "no weapon is starting; one must be");
    return;
  }
  for (const extra of starting.slice(1)) {
    report({ table: "weapons", id: extra.name, problems }, `a second starting weapon; ${first.name} starts already`);
  }
}

/**
 * The weapon that names each ability a weapon's blow names, read from the raw weapons table before any record is
 * checked, so an item's use is judged against every weapon whatever order the tables are read in.
 *
 * @param records The weapons table.
 * @returns Each named ability's id and the first weapon naming it, by the weapon's id or its place.
 */
function weaponsNaming(records: readonly unknown[] | undefined): ReadonlyMap<string, string> {
  const naming = new Map<string, string>();
  for (const [index, record] of (records ?? []).entries()) {
    const blows: unknown = isPlainRecord(record) ? Object.getOwnPropertyDescriptor(record, "blows")?.value : undefined;
    if (!Array.isArray(blows)) {
      continue;
    }
    for (const blow of blows) {
      const ability: unknown = isPlainRecord(blow) ? Object.getOwnPropertyDescriptor(blow, "ability")?.value : undefined;
      if (typeof ability === "string" && !naming.has(ability)) {
        naming.set(ability, recordName(record, index, "id"));
      }
    }
  }
  return naming;
}

/**
 * Reports a default bar that does not fit the bar's `BAR_SLOTS` slots (ADR-121 decision 2.2): the starting weapon's
 * blows, every given ability and the item slot, so a rules change that would overfill a new character's bar is refused.
 * Only records that passed their checks count.
 *
 * @param abilities The abilities that passed their checks.
 * @param weapons The weapons that passed their checks.
 * @param problems Where problems go.
 */
function checkBarSize(abilities: readonly Passed<Ability>[], weapons: readonly Passed<Weapon>[], problems: RulesProblem[]): void {
  const starting = weapons.find(({ record }) => record.starting);
  const held = (starting?.record.blows.length ?? 0) + abilities.filter(({ record }) => record.given).length + 1;
  if (held > BAR_SLOTS) {
    report({ table: "abilities", id: null, problems }, `the default bar holds ${held} slots, and the bar has ${BAR_SLOTS}: the starting weapon's blows, every given ability and the item slot (ADR-121 decision 2.2)`);
  }
}

/**
 * A count constant's checked value, for the checks that read one table against another.
 *
 * @param constants The constants that passed their checks.
 * @param id The count constant's id.
 * @returns Its count, or null when no record of the id passed.
 */
function countOf(constants: readonly Passed<Constant>[], id: CountConstantId): number | null {
  for (const { record } of constants) {
    if (record.id === id && "count" in record) {
      return record.count;
    }
  }
  return null;
}

/**
 * A tick constant's checked value, for the checks that read one table against another.
 *
 * @param constants The constants that passed their checks.
 * @param id The tick constant's id.
 * @returns Its ticks, or null when no record of the id passed.
 */
function tickOf(constants: readonly Passed<Constant>[], id: TickConstantId): number | null {
  for (const { record } of constants) {
    if (record.id === id && "ticks" in record) {
      return record.ticks;
    }
  }
  return null;
}

/**
 * Orders problems by their table's place among the tables, keeping each table's own order, so the checks that run
 * across a table after its records report beside them; problems of a table the set should not hold come first.
 *
 * @param problems The problems as found.
 * @returns The problems in order.
 */
function byTable(problems: readonly RulesProblem[]): RulesProblem[] {
  const order: readonly string[] = RULES_TABLES;
  return problems
    .map((problem, index) => ({ problem, index, rank: order.indexOf(problem.table) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ problem }) => problem);
}

/**
 * Validates a set of rules tables (ADR-069 decision 5).
 *
 * @param documents The twelve tables, as a record of lists: read from the build's JSON by the loader, or from
 *   anywhere a later loader reads them.
 * @returns The tables as the declared types, built from the checked fields, or every problem found, each naming
 *   its table and its record's id, in the tables' order and each table's own.
 * @example
 * validateRules({ abilities: [], backgrounds: [], constants: [], crits: [], effects: [], items: [], creature_levels: [], levels: [], looks: [], resources: [], weapons: [], xp_gap: [] }).ok;
 * // false: every background, constant, crit and resource is missing, no level, no look a player may choose, no starting weapon
 */
export function validateRules(documents: unknown): RulesVerdict {
  if (!isPlainRecord(documents)) {
    return { ok: false, problems: [{ table: "rules", id: null, message: "the documents are not a record of tables" }] };
  }
  const problems: RulesProblem[] = [];
  for (const key of Object.keys(documents)) {
    if (!(RULES_TABLES as readonly string[]).includes(key)) {
      problems.push({ table: key, id: null, message: `${key} is not a rules table; the tables are ${listed(RULES_TABLES)}` });
    }
  }
  const lists: Partial<Record<RulesTableName, readonly unknown[]>> = {};
  for (const table of RULES_TABLES) {
    const place: Place = { table, id: null, problems };
    const descriptor = Object.getOwnPropertyDescriptor(documents, table);
    if (descriptor === undefined) {
      report(place, "the table is missing");
      continue;
    }
    const list: unknown = descriptor.value;
    if (descriptor.get !== undefined || descriptor.set !== undefined || !Array.isArray(list)) {
      const foreign = descriptor.get !== undefined || descriptor.set !== undefined ? { path: "", what: "a getter" } : foreignValue(list, "");
      report(place, foreign !== null ? foreignMessage(foreign, "the table") : "the table is not a list of records");
      continue;
    }
    lists[table] = list;
  }
  const effectIds = namesIn(lists.effects);
  const abilityIds = namesIn(lists.abilities);
  const givenIds = namesIn(lists.abilities?.filter((record) => isPlainRecord(record) && record["given"] === true));
  const selfIds = namesIn(lists.abilities?.filter((record) => isPlainRecord(record) && isPlainRecord(record["shape"]) && record["shape"]["kind"] === "self"));
  const abilities = checkRecords("abilities", lists.abilities ?? [], problems, (place, record) => abilityOf(place, record, effectIds));
  const backgrounds = checkRecords("backgrounds", lists.backgrounds ?? [], problems, backgroundOf);
  const constants = checkRecords("constants", lists.constants ?? [], problems, constantOf);
  const crits = checkRecords("crits", lists.crits ?? [], problems, critOf);
  const effects = checkRecords("effects", lists.effects ?? [], problems, effectOf);
  const records = new Map(abilities.map(({ record }) => [record.id, record]));
  const items = checkItems(lists.items ?? [], problems, { abilityIds, records, givenIds, selfIds, weaponOf: weaponsNaming(lists.weapons) });
  const levels = checkRecords("levels", lists.levels ?? [], problems, levelOf, "level");
  const creatureLevels = checkRecords("creature_levels", lists.creature_levels ?? [], problems, creatureLevelOf, "level");
  const xpGap = checkRecords("xp_gap", lists.xp_gap ?? [], problems, xpGapOf);
  const looks = lists.looks === undefined ? [] : checkLooks(lists.looks, problems);
  const resources = checkRecords("resources", lists.resources ?? [], problems, resourceOf);
  const weapons = checkRecords("weapons", lists.weapons ?? [], problems, (place, record) => weaponOf(place, record, abilityIds, givenIds));
  if (lists.backgrounds !== undefined) {
    reportMissing({ table: "backgrounds", id: null, problems }, namesIn(lists.backgrounds), BACKGROUND_IDS);
  }
  if (lists.constants !== undefined) {
    reportMissing({ table: "constants", id: null, problems }, namesIn(lists.constants), CONSTANT_IDS);
  }
  if (lists.resources !== undefined) {
    reportMissing({ table: "resources", id: null, problems }, namesIn(lists.resources), RESOURCE_IDS);
  }
  if (lists.crits !== undefined) {
    reportMissing({ table: "crits", id: null, problems }, namesIn(lists.crits), CRIT_IDS);
  }
  if (lists.levels !== undefined) {
    checkLevelsRun("levels", lists.levels, problems);
    checkLevelsClimb(levels.map(({ record }) => record), problems);
  }
  if (lists.creature_levels !== undefined) {
    checkLevelsRun("creature_levels", lists.creature_levels, problems);
    checkCreatureLevels(creatureLevels, levels.map(({ record }) => record), problems);
  }
  if (lists.xp_gap !== undefined) {
    if (lists.xp_gap.length === 0) {
      report({ table: "xp_gap", id: null, problems }, "the table holds no row; it starts at gap 0");
    }
    checkXpGap(xpGap, problems);
  }
  if (lists.weapons !== undefined) {
    checkStarting(weapons, problems);
  }
  checkBarSize(abilities, weapons, problems);
  checkPenalties(effects, problems);
  checkOnHit(abilities, effects, problems);
  checkDiminishingWindow(tickOf(constants, "diminishing_window"), abilities, effects, problems);
  checkProjectileDodge(
    abilities.map(({ record }) => record),
    countOf(constants, "walk_speed"),
    problems,
  );
  checkDiagonalMoves(countOf(constants, "walk_speed"), countOf(constants, "creature_speed"), problems);
  checkRunSpeed(countOf(constants, "walk_speed"), countOf(constants, "run_speed"), problems);
  checkXpRates(countOf(constants, "xp_percent_protected"), countOf(constants, "xp_percent_flagged"), countOf(constants, "xp_percent_hostile"), problems);
  checkAttributeCap(countOf(constants, "attribute_start"), countOf(constants, "attribute_cap"), problems);
  checkTravelFocus(countOf(constants, "travel_anywhere_focus_percent"), countOf(constants, "travel_anywhere_focus_cap"), problems);
  checkRunRestart(
    countOf(constants, "run_restart"),
    levels.map(({ record }) => record),
    problems,
  );
  checkProjectileWeapons(
    abilities.map(({ record }) => record),
    weaponsNaming(lists.weapons),
    problems,
  );
  if (problems.length > 0) {
    return { ok: false, problems: byTable(problems) };
  }
  return {
    ok: true,
    tables: {
      abilities: abilities.map(({ record }) => record),
      backgrounds: backgrounds.map(({ record }) => record),
      constants: constants.map(({ record }) => record),
      creature_levels: creatureLevels.map(({ record }) => record),
      crits: crits.map(({ record }) => record),
      effects: effects.map(({ record }) => record),
      items,
      levels: levels.map(({ record }) => record),
      looks,
      resources: resources.map(({ record }) => record),
      weapons: weapons.map(({ record }) => record),
      xp_gap: xpGap.map(({ record }) => record),
    },
  };
}
